import {
    Button,
    Modal,
    ModalBody,
    ModalFooter,
    ModalHeader,
    Text,
    ThemeFontSizeVariable,
    ThemeSpaceVariable,
} from "@caspeco/casper-ui-library";
import { WebBookingErrorCode } from "context/error-context";
import { useBookingActionsContext, useBookingStateContext } from "hooks/use-booking-state";
import { useError } from "hooks/use-error";
import { PageType } from "logger/page-tracking";
import { usePageTracking } from "logger/use-page-tracking";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Routes } from "routes";
import { getSearchParams, UrlParamName } from "search-params";
import { isGuid } from "utils";

export default function ReservationFailedModal() {
    const { t } = useTranslation();
    const { errorCode, setErrorCode, isOpenReservationFailedModal, closeReservationFailedModal } =
        useError();
    const { setState } = useBookingActionsContext();
    const { guests, guestsChildren } = useBookingStateContext();
    const navigate = useNavigate();

    usePageTracking({
        pageName: "ReservationFailedError",
        pageType: PageType.Modal,
        autoTrack: isOpenReservationFailedModal,
        dependencies: [isOpenReservationFailedModal],
        metadata: {
            errorCode: WebBookingErrorCode[errorCode],
            errorCodeValue: errorCode,
        },
    });

    const handleGoToSelectDate = async () => {
        setState?.((prevState) => ({
            ...prevState,
            date: undefined,
            times: undefined,
        }));

        // An invitation (VIP) link has to survive the retry, or the experience it unlocks disappears.
        const { invite } = getSearchParams();
        const inviteParam = invite ? `&${UrlParamName.INVITE}=${encodeURIComponent(invite)}` : "";
        // The booking id goes too: the date step has no page that takes one.
        const leftSteps = new Set<string>([Routes.AVAILABILITY, Routes.FINALIZE]);
        const pathname = location.pathname
            .split("/")
            .filter((segment) => !leftSteps.has(segment) && !isGuid(segment))
            .join("/");
        await navigate({
            pathname,
            search: `?guests=${guests}&children=${guestsChildren}${inviteParam}`,
        });

        setErrorCode(WebBookingErrorCode.Undefined);
        closeReservationFailedModal();
    };

    const getMessageText = () => {
        switch (errorCode) {
            case WebBookingErrorCode.TemporaryUnavailable:
                return t("reservationFailed.message.notAvailable", { action: getActionText() });
            case WebBookingErrorCode.Undefined:
                return t("reservationFailed.message.unknown", { action: getActionText() });
            default:
                return;
        }
    };

    const getActionText = () => {
        switch (errorCode) {
            case WebBookingErrorCode.TemporaryUnavailable:
                return t("selectDateAgain");
            default:
                return t("selectDateAgain");
        }
    };

    const getButtonText = () => {
        switch (errorCode) {
            case WebBookingErrorCode.TemporaryUnavailable:
                return t("goToSelectDate");
            default:
                return t("goToSelectDate");
        }
    };
    return (
        <Modal
            isOpen={isOpenReservationFailedModal}
            onClose={() => void handleGoToSelectDate()}
            size="md"
            closeOnOverlayClick={false}
            closeOnEsc={false}
        >
            <ModalHeader
                display="flex"
                justifyContent="space-between"
                p={ThemeSpaceVariable.Medium}
            >
                <Text fontWeight="medium" fontSize={ThemeFontSizeVariable.XLarge}>
                    {t("reservationFailed.title")}
                </Text>
            </ModalHeader>
            <ModalBody
                display="flex"
                flexDirection="column"
                px={[ThemeSpaceVariable.Medium, ThemeSpaceVariable.Large]}
            >
                <Text>{getMessageText()}</Text>
            </ModalBody>
            <ModalFooter
                px={ThemeSpaceVariable.Medium}
                pt={ThemeSpaceVariable.Medium}
                pb={ThemeSpaceVariable.Large}
            >
                <Button
                    variant="primary"
                    size="xl"
                    alignSelf="flex-end"
                    onClick={() => void handleGoToSelectDate()}
                    data-track-click="error-reservation-failed-select-date"
                >
                    {getButtonText()}
                </Button>
            </ModalFooter>
        </Modal>
    );
}
